'use client';
import React, { MouseEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { CalendarPicker, LocalizationProvider, PickersDayProps } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Popover from '@mui/material/Popover';
import format from 'date-fns/format';
import fr from 'date-fns/locale/fr';
import { Button, Icon, IconButton, Stack, TextField } from 'ui-components';
import { DateRange } from './date-range.interface';
import { DateRangePickerProps } from './date-range-picker-props.interface';
import { buildRangeFromSelectedDay, isDayWithinRange, isSameCalendarDay } from './date-range.utils';
import RangeDay from './range-day';
import { CALENDAR_SX } from './date-range-picker.constants';

const DISPLAY_FORMAT = 'dd/MM/yyyy';
const EMPTY_BOUNDARY_LABEL = '…';

const DateRangePicker = (props: DateRangePickerProps): ReactElement => {
  const {
    value,
    onChange,
    placeholder,
    size,
    shouldOpenOnMount,
    shouldDisableFuture,
    popoverElevation,
    shouldShowApplyButton,
  } = props;
  const { start, end } = value;
  const fieldRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  useEffect(() => {
    if (shouldOpenOnMount) {
      setAnchorEl(fieldRef.current);
    }
  }, []);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDaySelect = (selectedDay: Date | null) => {
    const isSelectable = Boolean(selectedDay);

    if (!isSelectable) return;

    const nextRange: DateRange = buildRangeFromSelectedDay(value, selectedDay as Date);
    const isRangeComplete = Boolean(nextRange.start && nextRange.end);

    onChange(nextRange);

    if (isRangeComplete) {
      handleClose();
    }
  };

  const handleClear = () => {
    const clearedRange: DateRange = { start: null, end: null };

    onChange(clearedRange);
  };

  const renderRangeDay = (day: Date, selectedDays: Date[], pickersDayProps: PickersDayProps<Date>): ReactElement => {
    const isWithinRange = isDayWithinRange(day, start, end);
    const isRangeStart = isSameCalendarDay(day, start);
    const isRangeEnd = isSameCalendarDay(day, end);

    const rangeDay: ReactElement = (
      <RangeDay
        {...pickersDayProps}
        isWithinRange={isWithinRange}
        isRangeStart={isRangeStart}
        isRangeEnd={isRangeEnd}
      />
    );

    return rangeDay;
  };

  const formatBoundary = (boundary: Date | null): string => {
    let formatted = '';

    if (boundary) {
      formatted = format(boundary, DISPLAY_FORMAT);
    }

    return formatted;
  };

  const hasValue = Boolean(start || end);
  const startLabel = formatBoundary(start) || EMPTY_BOUNDARY_LABEL;
  const endLabel = formatBoundary(end) || EMPTY_BOUNDARY_LABEL;
  let rangeLabel = '';

  if (hasValue) {
    rangeLabel = `${startLabel} - ${endLabel}`;
  }

  return (
    <>
      <div ref={fieldRef}>
        <TextField
          value={rangeLabel}
          placeholder={placeholder}
          size={size}
          onClick={handleOpen}
          inputProps={{ readOnly: true, style: { cursor: 'pointer', textAlign: 'left' } }}
          endAdornment={
            <IconButton size="small" onClick={handleOpen}>
              <Icon name="calendar" size="small" />
            </IconButton>
          }
          fullWidth
        />
      </div>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        elevation={popoverElevation}
        sx={CALENDAR_SX}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
          <Stack>
            <CalendarPicker<Date>
              date={start}
              onChange={handleDaySelect}
              renderDay={renderRangeDay}
              disableFuture={shouldDisableFuture}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1} px={2} pb={1}>
              <Button color="tertiary" size="small" disabled={!hasValue} onClick={handleClear}>
                Effacer les dates
              </Button>
              {shouldShowApplyButton && (
                <Button color="tertiary" size="small" variant="contained" onClick={handleClose}>
                  Appliquer
                </Button>
              )}
            </Stack>
          </Stack>
        </LocalizationProvider>
      </Popover>
    </>
  );
};

export default DateRangePicker;
